import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { KitchenHoldOut } from "../../../../../api/billKitchenHold";
import { serverNowMs } from "../../../../../lib/serverClock";
import {
  isKitchenHoldExpired,
  kitchenHoldRemainingMinutes,
  kitchenHoldRemainingMs,
} from "../../../../../lib/kitchenHold";
import { InfinityIcon } from "../../menu/KitchenHoldTimeOptions";

/** „09:11" — часът, в който задържаната влиза сама (местното време на срока). */
const formatReleaseTime = (holdUntil: string): string =>
  new Date(holdUntil).toLocaleTimeString("bg-BG", { hour: "2-digit", minute: "2-digit" });

/** Цели минути от задържането (надолу); null при неразчетим момент. */
const heldMinutesOf = (heldAt: string, nowMs: number): number | null => {
  const heldAtMs = Date.parse(heldAt);
  return Number.isFinite(heldAtMs) ? Math.max(0, Math.floor((nowMs - heldAtMs) / 60_000)) : null;
};

/**
 * Кога задържаната партида влиза в кухнята: часът в акцента и под него „след
 * 29 мин" (`stacked` — колоната вляво от редовете на десктоп) или хапче
 * „09:11 · след 29 мин" (`inline` — в разделителя на тесния вид, до часа на
 * маркиране — хапчето ги различава; ⏸ е бутонът „Промени" до него; при
 * съвсем тесен ред надписът се реже, бутоните остават). Ръчно
 * задържаната — ∞ и ОТ КОЛКО време чака („от 14 мин"; под минута — „току-
 * що"); при 0 — „Влиза…" (пуска я BE по своя таймер, сметките се опресняват
 * отделно — `useKitchenHoldExpiryRefresh`).
 *
 * Времената са по часовника на СЪРВЪРА (`lib/serverClock.ts`), защото
 * `hold_until`/`held_at` са от базата; тик 1 s в собствен компонент, за да не
 * прерисува целия детайл на масата, и от абсолютния момент — заспал таб се
 * събужда с вярното време.
 */
export const KitchenHoldDeadline = ({
  hold,
  variant,
}: {
  hold: KitchenHoldOut;
  variant: "stacked" | "inline";
}) => {
  const { t } = useTranslation();
  const [nowMs, setNowMs] = useState(serverNowMs);
  const manual = hold.hold_until == null;
  const remainingMs = kitchenHoldRemainingMs(hold.hold_until, nowMs);
  const expired = remainingMs !== null && isKitchenHoldExpired(remainingMs);
  // Изтеклата не тиктака — чака опресняването.
  const ticking = !expired;

  useEffect(() => {
    if (!ticking) return;
    const intervalId = window.setInterval(() => setNowMs(serverNowMs()), 1000);
    return () => window.clearInterval(intervalId);
  }, [ticking]);

  const heldMinutes = manual ? heldMinutesOf(hold.held_at, nowMs) : null;
  const main = expired
    ? t("ordering.kitchenHold.entering")
    : manual
      ? null
      : formatReleaseTime(hold.hold_until!);
  const secondary = manual
    ? heldMinutes === null
      ? null
      : heldMinutes === 0
        ? t("ordering.kitchenHold.heldJustNow")
        : t("ordering.kitchenHold.heldFor", { count: heldMinutes })
    : remainingMs !== null && !expired
      ? t("ordering.kitchenHold.releaseIn", { count: kitchenHoldRemainingMinutes(remainingMs) })
      : null;

  const mainClass = "font-pos-mono font-bold text-pos-accent-ink tabular-nums";
  // Ръчно задържаната — тънкият ∞ (не удебелен) с подсказката „Ръчно — …".
  const infinity = (className: string) => (
    <span
      role="img"
      title={t("ordering.kitchenHold.manualHint")}
      aria-label={t("ordering.kitchenHold.manualShort")}
      className="inline-flex shrink-0 text-pos-accent-ink"
    >
      <InfinityIcon className={className} />
    </span>
  );
  if (variant === "inline") {
    return (
      <span className="inline-flex h-6 min-w-0 items-center gap-0.5 whitespace-nowrap rounded-[7px] border border-pos-accent/40 bg-pos-marked px-1 text-[11px]">
        {main === null ? infinity("w-[18px]") : <span className={`${mainClass} shrink-0`}>{main}</span>}
        {secondary && <span className="min-w-0 truncate text-pos-soft">· {secondary}</span>}
      </span>
    );
  }
  // Колоната вляво: ∞ е по-едър и центриран над „от 14 мин" — съразмерен с
  // часа „09:11" при таймер, който е подравнен вляво.
  return main === null ? (
    <span className="inline-flex flex-col items-center gap-0.5 leading-tight">
      {infinity("w-7")}
      {secondary && <span className="text-[11px] text-pos-muted">{secondary}</span>}
    </span>
  ) : (
    <span className="flex flex-col leading-tight">
      <span className={`${mainClass} text-[12.5px]`}>{main}</span>
      {secondary && <span className="text-[11px] text-pos-muted">{secondary}</span>}
    </span>
  );
};
